import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { Card, Col, Row, Select, Button, Flex, Input, DatePicker, Popover, Typography } from 'antd';

import { ReviewDecision } from 'models/predictions';
import { AdvancedFilterValue } from 'models/advancedFilters';
import { Text } from 'theme/typography';
import { TYPOGRAPHY_SIZES } from 'theme';
import { usePredictionFilterState } from 'hooks/useFilterState';
import { useGetSegmentationClassesQuery } from '../../../redux/slices/views';

import './predictionFilters.css';
import '../../molecules/advancedFilters/styles.css';

const { Option } = Select;

interface PredictionFiltersProps {
  onFiltersApplied: () => void;
  onFiltersClear: () => void;
}

const PredictionFilters: React.FC<PredictionFiltersProps> = ({
  onFiltersApplied,
  onFiltersClear,
}) => {
  const { filterState, setPredictionFilter } = usePredictionFilterState();
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch segmentation classes for features
  const {
    data: segmentationClasses,
    isLoading: isLoadingSegmentationClasses,
    isError: isSegmentationClassesError,
  } = useGetSegmentationClassesQuery({});

  const [featureOptions, setFeatureOptions] = useState<AdvancedFilterValue[]>([]);
  const [datasourceOptions] = useState<AdvancedFilterValue[]>([
    { uuid: 'ABBOTT', name: 'ABBOTT' },
    { uuid: 'ARKHEART', name: 'ARKHEART' },
    { uuid: 'CONWAY', name: 'CONWAY' },
    { uuid: 'DP', name: 'DP' },
    { uuid: 'FSV', name: 'FSV' },
  ]);

  const [pendingFilters, setPendingFilters] = useState({
    is_reviewed: filterState.is_reviewed,
    review_decision: filterState.review_decision,
    has_corresponding_label: filterState.has_corresponding_label,
    features: filterState.features,
    datasource: filterState.datasource,
    dicom_icid: filterState.dicom_icid,
    source_uuid: (filterState as any).source_uuid,
    segmentation_prediction_icid: (filterState as any).segmentation_prediction_icid,
    segmentation_label_icid: (filterState as any).segmentation_label_icid,
    study_icid: filterState.study_icid,
    study_fid: (filterState as any).study_fid,
    created_at_after: filterState.created_at_after,
    created_at_before: filterState.created_at_before,
    label_created_at_after: filterState.label_created_at_after,
    label_created_at_before: filterState.label_created_at_before,
  });

  // Populate feature options from segmentation-classes endpoint
  useEffect(() => {
    if (!segmentationClasses) {
      setFeatureOptions([]);
      return;
    }
    let items: any[] = [];
    if (Array.isArray(segmentationClasses)) {
      items = segmentationClasses as any[];
    } else if (
      typeof segmentationClasses === 'object' &&
      'results' in (segmentationClasses as any) &&
      Array.isArray((segmentationClasses as any).results)
    ) {
      items = (segmentationClasses as any).results;
    }
    const sorted = [...items].sort((a, b) => String(a.value).localeCompare(String(b.value)));
    const features: AdvancedFilterValue[] = sorted.map((item: any) => ({
      uuid: item.uuid,
      name: item.value,
      acronym: item.acronym,
    }));
    setFeatureOptions(features);
  }, [segmentationClasses]);

  // Initialize filters from URL on mount
  useEffect(() => {
    const isReviewed = searchParams.get('is_reviewed');
    const reviewDecision = searchParams.get('review_decision');
    const hasLabel = searchParams.get('has_corresponding_label');
    const featuresParam = searchParams.get('features');
    const datasourceParam = searchParams.get('datasource');
    const dicomParam = searchParams.get('dicom');
    // const dicomFidParam = searchParams.get('dicomFid') || searchParams.get('dicom_fid');
    const studyParam = searchParams.get('study');
    const studyFidParam = searchParams.get('studyFid') || searchParams.get('study_fid');
    const sourceUuidParam = searchParams.get('source_uuid');
    const segPredIcidParam =
      searchParams.get('segmentation_prediction_icid') ||
      searchParams.get('segmentationPredictionICID') ||
      searchParams.get('segmentationPredictionIcid');
    const segLabelIcidParam =
      searchParams.get('segmentation_label_icid') ||
      searchParams.get('segmentationLabelICID') ||
      searchParams.get('segmentationLabelIcid');
    const createdAtAfter = searchParams.get('created_at_after');
    const createdAtBefore = searchParams.get('created_at_before');
    const labelCreatedAtAfter = searchParams.get('label_created_at_after');
    const labelCreatedAtBefore = searchParams.get('label_created_at_before');

    const initialFilters: any = {};
    let hasFilters = false;

    if (isReviewed !== null) {
      initialFilters.is_reviewed = isReviewed === 'true';
      hasFilters = true;
    }

    if (reviewDecision) {
      initialFilters.review_decision = reviewDecision as ReviewDecision;
      hasFilters = true;
    }

    if (hasLabel !== null) {
      initialFilters.has_corresponding_label = hasLabel === 'true';
      hasFilters = true;
    }

    if (featuresParam && featureOptions.length > 0) {
      const requested = featuresParam
        .split(',')
        .map(f => decodeURIComponent(f))
        .map(f => f.replace(/\+/g, ' '));
      const selectedFeatures = featureOptions.filter(
        option => requested.includes(option.name) || requested.includes(option.uuid)
      );
      if (selectedFeatures.length > 0) {
        initialFilters.features = selectedFeatures;
        hasFilters = true;
      }
    }

    if (datasourceParam) {
      const selectedDatasource = datasourceOptions.find(
        option => option.name === datasourceParam || option.uuid === datasourceParam
      );
      if (selectedDatasource) {
        initialFilters.datasource = selectedDatasource;
        hasFilters = true;
      }
    }

    if (dicomParam) {
      initialFilters.dicom_icid = dicomParam;
      hasFilters = true;
    }

    if (studyParam) {
      initialFilters.study_icid = studyParam;
      hasFilters = true;
    }

    // if (dicomFidParam) {
    //   initialFilters.dicom_fid = dicomFidParam;
    //   hasFilters = true;
    // }

    if (studyFidParam) {
      initialFilters.study_fid = studyFidParam;
      hasFilters = true;
    }
    if (sourceUuidParam) {
      initialFilters.source_uuid = sourceUuidParam;
      hasFilters = true;
    }

    if (segPredIcidParam) {
      initialFilters.segmentation_prediction_icid = segPredIcidParam;
      hasFilters = true;
    }

    if (segLabelIcidParam) {
      initialFilters.segmentation_label_icid = segLabelIcidParam;
      hasFilters = true;
    }

    if (createdAtAfter) {
      initialFilters.created_at_after = createdAtAfter;
      hasFilters = true;
    }

    if (createdAtBefore) {
      initialFilters.created_at_before = createdAtBefore;
      hasFilters = true;
    }

    if (labelCreatedAtAfter) {
      initialFilters.label_created_at_after = labelCreatedAtAfter;
      hasFilters = true;
    }

    if (labelCreatedAtBefore) {
      initialFilters.label_created_at_before = labelCreatedAtBefore;
      hasFilters = true;
    }

    if (hasFilters) {
      setPendingFilters(prev => ({ ...prev, ...initialFilters }));
      Object.entries(initialFilters).forEach(([key, value]) => {
        setPredictionFilter(key, value);
      });
      onFiltersApplied();
    }
  }, [featureOptions, datasourceOptions]);

  const getReviewStatusValue = () => {
    if (pendingFilters.is_reviewed === true) return 'reviewed';
    if (pendingFilters.is_reviewed === false) return 'unreviewed';
    return undefined;
  };

  const handleReviewStatusChange = (value: string) => {
    if (value === 'reviewed') {
      handlePredictionFilterChange('is_reviewed', true);
    } else if (value === 'unreviewed') {
      handlePredictionFilterChange('is_reviewed', false);
    } else {
      handlePredictionFilterChange('is_reviewed', undefined);
    }
  };

  // Update URL with filter params (preserve non-filter params like blinded)
  const updateURLParams = () => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params but preserve others (like blinded)
    const filterKeys = [
      'is_reviewed',
      'review_decision',
      'has_corresponding_label',
      'features',
      'datasource',
      'dicom',
      'segmentation_prediction_icid',
      'segmentation_label_icid',
      'source_uuid',
      'study',
      'study_fid',
      'created_at_after',
      'created_at_before',
      'label_created_at_after',
      'label_created_at_before',
    ];
    filterKeys.forEach(key => params.delete(key));

    if (pendingFilters.is_reviewed !== undefined) {
      params.set('is_reviewed', pendingFilters.is_reviewed.toString());
    }

    if (pendingFilters.review_decision) {
      params.set('review_decision', pendingFilters.review_decision);
    }

    if (pendingFilters.has_corresponding_label !== undefined) {
      params.set('has_corresponding_label', pendingFilters.has_corresponding_label.toString());
    }

    if (pendingFilters.features && pendingFilters.features.length > 0) {
      const featuresString = pendingFilters.features
        .map(f => encodeURIComponent(f.name.replace(/ /g, '+')))
        .join(',');
      params.set('features', featuresString);
    }

    if (pendingFilters.datasource) {
      params.set('datasource', pendingFilters.datasource.name);
    }

    if (pendingFilters.dicom_icid) {
      params.set('dicom', pendingFilters.dicom_icid);
    }

    if ((pendingFilters as any).segmentation_prediction_icid) {
      params.set(
        'segmentation_prediction_icid',
        (pendingFilters as any).segmentation_prediction_icid
      );
    }

    if ((pendingFilters as any).segmentation_label_icid) {
      params.set('segmentation_label_icid', (pendingFilters as any).segmentation_label_icid);
    }

    // prefer explicit FID param if provided
    // if ((pendingFilters as any).dicom_fid) {
    //   params.set('dicom', (pendingFilters as any).dicom_fid);
    // }

    if (pendingFilters.source_uuid) {
      params.set('source_uuid', pendingFilters.source_uuid);
    }

    if (pendingFilters.study_icid) {
      params.set('study', pendingFilters.study_icid);
    }

    if ((pendingFilters as any).study_fid) {
      // Use explicit study_fid param so backend can use the FID filter
      params.set('study_fid', (pendingFilters as any).study_fid);
    }

    if (pendingFilters.created_at_after) {
      params.set('created_at_after', pendingFilters.created_at_after);
    }

    if (pendingFilters.created_at_before) {
      params.set('created_at_before', pendingFilters.created_at_before);
    }

    if (pendingFilters.label_created_at_after) {
      params.set('label_created_at_after', pendingFilters.label_created_at_after);
    }

    if (pendingFilters.label_created_at_before) {
      params.set('label_created_at_before', pendingFilters.label_created_at_before);
    }

    setSearchParams(params);
  };

  const handleApply = () => {
    // Apply pending filters to actual filter state
    Object.entries(pendingFilters).forEach(([key, value]) => {
      setPredictionFilter(key, value);
    });

    updateURLParams();

    onFiltersApplied();
  };

  const handlePredictionFilterChange = (key: string, value: any) => {
    setPendingFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleClearPredictionFilters = () => {
    // Clear pending filters
    setPendingFilters({
      is_reviewed: undefined,
      review_decision: undefined,
      has_corresponding_label: undefined,
      features: undefined,
      datasource: undefined,
      dicom_icid: undefined,
      segmentation_prediction_icid: undefined,
      segmentation_label_icid: undefined,
      source_uuid: undefined,
      // dicom_fid: undefined,
      study_icid: undefined,
      study_fid: undefined,
      created_at_after: undefined,
      created_at_before: undefined,
      label_created_at_after: undefined,
      label_created_at_before: undefined,
    });

    // Clear actual filters
    setPredictionFilter('is_reviewed', undefined);
    setPredictionFilter('review_decision', undefined);
    setPredictionFilter('has_corresponding_label', undefined);
    setPredictionFilter('features', undefined);
    setPredictionFilter('datasource', undefined);
    setPredictionFilter('dicom_icid', undefined);
    setPredictionFilter('segmentation_prediction_icid', undefined);
    setPredictionFilter('segmentation_label_icid', undefined);
    setPredictionFilter('source_uuid', undefined);
    // setPredictionFilter('dicom_fid', undefined);
    setPredictionFilter('study_icid', undefined);
    setPredictionFilter('study_fid', undefined);
    setPredictionFilter('created_at_after', undefined);
    setPredictionFilter('created_at_before', undefined);
    setPredictionFilter('label_created_at_after', undefined);
    setPredictionFilter('label_created_at_before', undefined);

    // Clear URL params
    setSearchParams({});

    // Notify parent component that filters were cleared
    onFiltersClear();
  };

  return (
    <Card className="prediction-filters-card">
      <Row gutter={[16, 16]} align="bottom">
        <Col xs={24} sm={12} md={6}>
          <div className="filter-group">
            <Text size={TYPOGRAPHY_SIZES.SMALL} weight={600}>
              Review Status
            </Text>
            <Select
              placeholder="All predictions"
              value={getReviewStatusValue()}
              onChange={handleReviewStatusChange}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="reviewed">Reviewed Only</Option>
              <Option value="unreviewed">Unreviewed Only</Option>
            </Select>
          </div>
        </Col>

        {/* Features (Segmentation Classes) */}
        <Col xs={24} sm={12} md={10}>
          <div className="filter-group">
            <Text size={TYPOGRAPHY_SIZES.SMALL} weight={600}>
              Features
            </Text>
            <Select
              mode="multiple"
              placeholder="Select features"
              value={pendingFilters.features?.map(f => f.uuid)}
              onChange={(values: string[]) => {
                const selectedFeatures = values
                  .map(value => featureOptions.find(option => option.uuid === value))
                  .filter(Boolean) as AdvancedFilterValue[];
                handlePredictionFilterChange(
                  'features',
                  selectedFeatures.length > 0 ? selectedFeatures : undefined
                );
              }}
              allowClear
              loading={isLoadingSegmentationClasses}
              disabled={isSegmentationClassesError}
              showSearch
              filterOption={(input, option) => {
                // Search by the display name/value, not the uuid
                const optionText = option?.children?.toString().toLowerCase() || '';
                return optionText.includes(input.toLowerCase());
              }}
              style={{ width: '100%' }}
            >
              {featureOptions.map(option => (
                <Option key={option.uuid} value={option.uuid}>
                  {option.name}
                </Option>
              ))}
            </Select>
          </div>
        </Col>

        {/* Advanced Filters Button */}
        <Col xs={24} sm={24} md={8}>
          <Flex vertical={false} justify="flex-end" style={{ width: '100%' }}>
            <Popover
              trigger="click"
              content={
                <Row gutter={[16, 16]} style={{ width: 400 }}>
                  {/* Review Decision */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>Review Decision</Typography.Text>
                      <Select
                        placeholder="All decisions"
                        value={pendingFilters.review_decision}
                        onChange={value => handlePredictionFilterChange('review_decision', value)}
                        allowClear
                        style={{ width: '100%' }}
                      >
                        <Option value={ReviewDecision.ACCEPT_AI}>Accept AI</Option>
                        <Option value={ReviewDecision.ACCEPT_HUMAN}>Accept Human</Option>
                        <Option value={ReviewDecision.ACCEPT_BOTH}>Accept Both</Option>
                        <Option value={ReviewDecision.ACCEPT_NEITHER}>Accept Neither</Option>
                      </Select>
                    </Flex>
                  </Col>

                  {/* AI Comparison */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>AI Comparison</Typography.Text>
                      <Select
                        placeholder="All predictions"
                        value={pendingFilters.has_corresponding_label}
                        onChange={value =>
                          handlePredictionFilterChange('has_corresponding_label', value)
                        }
                        allowClear
                        style={{ width: '100%' }}
                      >
                        <Option value={true}>Ready for Comparison</Option>
                        <Option value={false}>No Human Label</Option>
                      </Select>
                    </Flex>
                  </Col>

                  {/* Datasource */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>Datasource</Typography.Text>
                      <Select
                        placeholder="Select datasource"
                        value={pendingFilters.datasource?.uuid}
                        onChange={(value: string) => {
                          const selectedDatasource = datasourceOptions.find(
                            option => option.uuid === value
                          );
                          handlePredictionFilterChange('datasource', selectedDatasource);
                        }}
                        allowClear
                        showSearch
                        filterOption={(input, option) => {
                          const optionText = option?.children?.toString().toLowerCase() || '';
                          return optionText.includes(input.toLowerCase());
                        }}
                        style={{ width: '100%' }}
                      >
                        {datasourceOptions.map(option => (
                          <Option key={option.uuid} value={option.uuid}>
                            {option.name}
                          </Option>
                        ))}
                      </Select>
                    </Flex>
                  </Col>

                  {/* Prediction Source UUID */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>Prediction Source UUID</Typography.Text>
                      <Input
                        placeholder="comma-separated UUIDs"
                        value={(pendingFilters as any).source_uuid}
                        onChange={e =>
                          handlePredictionFilterChange('source_uuid', e.target.value || undefined)
                        }
                        allowClear
                        style={{ width: '100%' }}
                      />
                    </Flex>
                  </Col>

                  {/* DICOM ICID */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>DICOM ICID</Typography.Text>
                      <Input
                        placeholder="di-000-00-00,di-111-11-11"
                        value={pendingFilters.dicom_icid}
                        onChange={e =>
                          handlePredictionFilterChange('dicom_icid', e.target.value || undefined)
                        }
                        allowClear
                        style={{ width: '100%' }}
                      />
                    </Flex>
                  </Col>

                  {/* Segmentation Prediction ICID */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>Segmentation Prediction ICID</Typography.Text>
                      <Input
                        placeholder="se-0594-8F10-0E48"
                        value={(pendingFilters as any).segmentation_prediction_icid}
                        onChange={e =>
                          handlePredictionFilterChange(
                            'segmentation_prediction_icid',
                            e.target.value || undefined
                          )
                        }
                        allowClear
                        style={{ width: '100%' }}
                      />
                    </Flex>
                  </Col>

                  {/* Segmentation Label ICID */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>Segmentation Label ICID</Typography.Text>
                      <Input
                        placeholder="se-0594-8F10-0E48"
                        value={(pendingFilters as any).segmentation_label_icid}
                        onChange={e =>
                          handlePredictionFilterChange(
                            'segmentation_label_icid',
                            e.target.value || undefined
                          )
                        }
                        allowClear
                        style={{ width: '100%' }}
                      />
                    </Flex>
                  </Col>

                  {/* DICOM FID (UUID)
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>DICOM FID</Typography.Text>
                      <Input
                        placeholder="44f21ff5-d658-486d-8d5f-b6f652a7739d"
                        value={(pendingFilters as any).dicom_fid}
                        onChange={e =>
                          handlePredictionFilterChange('dicom_fid', e.target.value || undefined)
                        }
                        allowClear
                        style={{ width: '100%' }}
                      />
                    </Flex>
                  </Col>
                  */}

                  {/* Study ICID */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>Study ICID</Typography.Text>
                      <Input
                        placeholder="st-000-00-00,st-111-00-00"
                        value={pendingFilters.study_icid}
                        onChange={e =>
                          handlePredictionFilterChange('study_icid', e.target.value || undefined)
                        }
                        allowClear
                        style={{ width: '100%' }}
                      />
                    </Flex>
                  </Col>

                  {/* Study FID (UUID) */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>Study FID</Typography.Text>
                      <Input
                        placeholder="b807bf03-2826-44a4-ab99-273eced65495"
                        value={(pendingFilters as any).study_fid}
                        onChange={e =>
                          handlePredictionFilterChange('study_fid', e.target.value || undefined)
                        }
                        allowClear
                        style={{ width: '100%' }}
                      />
                    </Flex>
                  </Col>

                  {/* Created At Date Range */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>Created At</Typography.Text>
                      <DatePicker.RangePicker
                        value={[
                          pendingFilters.created_at_after
                            ? dayjs(pendingFilters.created_at_after)
                            : null,
                          pendingFilters.created_at_before
                            ? dayjs(pendingFilters.created_at_before)
                            : null,
                        ]}
                        onChange={(dates, dateStrings) => {
                          handlePredictionFilterChange(
                            'created_at_after',
                            dateStrings[0] || undefined
                          );
                          handlePredictionFilterChange(
                            'created_at_before',
                            dateStrings[1] || undefined
                          );
                        }}
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD"
                      />
                    </Flex>
                  </Col>

                  {/* Label Created At Date Range */}
                  <Col span={24}>
                    <Flex vertical>
                      <Typography.Text strong>Label Created At</Typography.Text>
                      <DatePicker.RangePicker
                        value={[
                          pendingFilters.label_created_at_after
                            ? dayjs(pendingFilters.label_created_at_after)
                            : null,
                          pendingFilters.label_created_at_before
                            ? dayjs(pendingFilters.label_created_at_before)
                            : null,
                        ]}
                        onChange={(dates, dateStrings) => {
                          handlePredictionFilterChange(
                            'label_created_at_after',
                            dateStrings[0] || undefined
                          );
                          handlePredictionFilterChange(
                            'label_created_at_before',
                            dateStrings[1] || undefined
                          );
                        }}
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD"
                      />
                    </Flex>
                  </Col>
                </Row>
              }
              placement="bottomLeft"
              arrow={false}
            >
              <Button type="link" className="advanced-filters-button">
                Advanced filters
              </Button>
            </Popover>
          </Flex>
        </Col>
      </Row>

      <Flex gap={8} justify="end" style={{ marginTop: 16 }}>
        <Button onClick={handleClearPredictionFilters}>Clear</Button>
        <Button onClick={handleApply} type="primary">
          Apply Filters
        </Button>
      </Flex>
    </Card>
  );
};

export default PredictionFilters;
